import { toast } from "sonner";
import { useState, useContext, useEffect } from "react";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Loader2 } from "lucide-react";

type PortalSource = {
  key: string;
  label: string;
  required: boolean;
};

type LocationSource = {
  locationSourceId: string;
  name: string;
  integration: boolean;
};

const PORTAL_SOURCES: PortalSource[] = [
  { key: "ciencuadras", label: "Ciencuadras", required: false },
  { key: "fincaraiz", label: "Finca Raíz", required: false },
  { key: "web", label: "Página Web", required: false },
  { key: "mercadolibre", label: "Mercado Libre", required: false },
  { key: "espaciourbano", label: "Espacio Urbano", required: false },
  { key: "metrocuadrado", label: "Metro Cuadrado", required: false },
  { key: "proppit", label: "Proppit", required: false },
  { key: "default", label: "Default", required: true },
];

type GlobalVariablesConfigurationProps = {
  crmConnection: any;
};

const GlobalVariablesConfiguration = ({
  crmConnection,
}: GlobalVariablesConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);

  const [locationSources, setLocationSources] = useState<
    Record<string, string | { in?: string; out?: string }>
  >({});
  const [availableLocationSources, setAvailableLocationSources] = useState<
    Array<{ locationSourceId: string; name: string }>
  >([]);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const utils = trpc.useUtils();
  const getLocationSources = trpc.crmSelfConfig.getLocationSources.useMutation({
    onSuccess: (result) => {
      if (result.success && result.locationSources) {
        setAvailableLocationSources(
          (result.locationSources as LocationSource[]).map((source) => ({
            locationSourceId: source.locationSourceId,
            name: source.name,
          }))
        );
        setIsLoadingSources(false);
      } else {
        toast.error("Error", {
          description: result.error || "No se pudieron obtener las fuentes de ubicación.",
        });
        setIsLoadingSources(false);
      }
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al obtener las fuentes de ubicación.",
      });
      setIsLoadingSources(false);
    },
  });

  const updateLocationSources = trpc.crmSelfConfig.updateLocationSources.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración guardada", {
        description: "Las variables globales se han guardado correctamente.",
      });
      setIsSaving(false);
      setIsEditing(false);
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al guardar las variables globales.",
      });
      setIsSaving(false);
    },
  });

  useEffect(() => {
    if (
      crmConnection?.provider === "smarthome-rentals" &&
      crmConnection?.smarthomeRentals?.companyCode &&
      availableLocationSources.length === 0 &&
      !isLoadingSources
    ) {
      setIsLoadingSources(true);
      getLocationSources.mutate({
        provider: "smarthome-rentals",
        companyCode: crmConnection.smarthomeRentals.companyCode,
      });
    }
  }, [
    crmConnection?.provider,
    crmConnection?.smarthomeRentals?.companyCode,
    availableLocationSources.length,
  ]);

  useEffect(() => {
    if (crmConnection?.smarthomeRentals?.locationSources) {
      const existingSources: Record<string, string | { in?: string; out?: string }> = {};
      PORTAL_SOURCES.forEach((portal) => {
        const value = crmConnection.smarthomeRentals.locationSources[portal.key];
        if (value) {
          existingSources[portal.key] = value;
        }
      });
      setLocationSources(existingSources);
    }
  }, [crmConnection?.smarthomeRentals?.locationSources]);

  const handleSourceChange = (portalKey: string, locationSourceId: string, type?: "in" | "out") => {
    setLocationSources((prev) => {
      if (portalKey === "default") {
        return {
          ...prev,
          [portalKey]: locationSourceId,
        };
      }

      const currentValue = prev[portalKey];
      const currentObj =
        typeof currentValue === "object" && currentValue !== null ? { ...currentValue } : {};

      if (type) {
        return {
          ...prev,
          [portalKey]: {
            ...currentObj,
            [type]: locationSourceId,
          },
        };
      }

      return {
        ...prev,
        [portalKey]: locationSourceId,
      };
    });
  };

  const handleSave = () => {
    if (!merchantId || !crmConnection?.id) return;

    const requiredSources = PORTAL_SOURCES.filter((p) => p.required);
    const missingRequired = requiredSources.find((p) => !locationSources[p.key]);

    if (missingRequired) {
      toast.error("Error", {
        description: `Por favor selecciona una fuente de ubicación para "${missingRequired.label}".`,
      });
      return;
    }

    setIsSaving(true);
    const ciencuadrasValue = locationSources.ciencuadras;
    const fincaraizValue = locationSources.fincaraiz;
    const webValue = locationSources.web;
    const mercadolibreValue = locationSources.mercadolibre;
    const espaciourbanoValue = locationSources.espaciourbano;
    const metrocuadradoValue = locationSources.metrocuadrado;
    const proppitValue = locationSources.proppit;

    const getLocationSourceValue = (
      value: string | { in?: string; out?: string } | undefined
    ): { in?: string; out?: string } | undefined => {
      if (typeof value === "object" && value !== null) {
        return value;
      }
      return undefined;
    };

    updateLocationSources.mutate({
      merchantId,
      id: crmConnection.id,
      locationSources: {
        ciencuadras: getLocationSourceValue(ciencuadrasValue),
        fincaraiz: getLocationSourceValue(fincaraizValue),
        web: getLocationSourceValue(webValue),
        mercadolibre: getLocationSourceValue(mercadolibreValue),
        espaciourbano: getLocationSourceValue(espaciourbanoValue),
        metrocuadrado: getLocationSourceValue(metrocuadradoValue),
        proppit: getLocationSourceValue(proppitValue),
        default: (locationSources.default as string) || "",
      },
    });
  };

  const isConfigurationComplete = () => {
    const requiredSources = PORTAL_SOURCES.filter((p) => p.required);
    return requiredSources.every((p) => locationSources[p.key]);
  };

  const hasConfiguration = () => {
    return !!(
      crmConnection?.smarthomeRentals?.locationSources?.default &&
      crmConnection.smarthomeRentals.locationSources.default.trim() !== ""
    );
  };

  const getLocationSourceName = (
    locationSourceId: string | undefined | { in?: string; out?: string }
  ): string => {
    if (!locationSourceId) return "";
    if (typeof locationSourceId === "object") {
      const inName = locationSourceId.in
        ? availableLocationSources.find((s) => s.locationSourceId === locationSourceId.in)?.name
        : "";
      const outName = locationSourceId.out
        ? availableLocationSources.find((s) => s.locationSourceId === locationSourceId.out)?.name
        : "";
      if (inName && outName) {
        return `IN: ${inName}, OUT: ${outName}`;
      } else if (inName) {
        return `IN: ${inName}`;
      } else if (outName) {
        return `OUT: ${outName}`;
      }
      return "";
    }
    const source = availableLocationSources.find((s) => s.locationSourceId === locationSourceId);
    return source?.name || "";
  };

  if (crmConnection?.provider !== "smarthome-rentals") {
    return <></>;
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-lg font-bold mb-2">
              Variables Globales
            </p>
            <p className="text-sm text-gray-600">
              Configura las fuentes de ubicación para cada portal. La fuente "Default" es
              obligatoria.
            </p>
          </div>
          <div className="flex flex-row gap-2">
            {hasConfiguration() ? (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                Configurado
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                Sin configurar
              </span>
            )}
            {!isEditing && (
              <Button
                variant="main"
                size="sm"
                onClick={() => {
                  setIsEditing(true);
                  // Load location sources if not already loaded
                  if (
                    availableLocationSources.length === 0 &&
                    crmConnection?.smarthomeRentals?.companyCode
                  ) {
                    setIsLoadingSources(true);
                    getLocationSources.mutate({
                      provider: "smarthome-rentals",
                      companyCode: crmConnection.smarthomeRentals.companyCode,
                    });
                  }
                }}
              >
                Editar
              </Button>
            )}
          </div>
        </div>

        {isEditing ? (
          <>
            {isLoadingSources ? (
              <div className="flex flex-row gap-2">
                <Spinner size="sm" />
                <p className="text-sm text-gray-500">
                  Cargando fuentes de ubicación...
                </p>
              </div>
            ) : availableLocationSources.length === 0 ? (
              <p className="text-sm text-gray-500">
                No se pudieron cargar las fuentes de ubicación.
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                {PORTAL_SOURCES.map((portal) => {
                  if (portal.key === "default") {
                    const defaultValue =
                      typeof locationSources[portal.key] === "string"
                        ? (locationSources[portal.key] as string)
                        : "";
                    return (
                      <div key={portal.key}>
                        <Label className="text-sm font-semibold text-gray-700">
                          {portal.label}
                          {portal.required && " *"}
                        </Label>
                        <select
                          value={defaultValue}
                          onChange={(e) => handleSourceChange(portal.key, e.target.value)}
                          className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                        >
                          <option value="">{`Selecciona una fuente para ${portal.label}`}</option>
                          {availableLocationSources.map((source) => (
                            <option key={source.locationSourceId} value={source.locationSourceId}>
                              {source.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  const sourceValue =
                    typeof locationSources[portal.key] === "object" &&
                    locationSources[portal.key] !== null
                      ? (locationSources[portal.key] as { in?: string; out?: string })
                      : { in: undefined, out: undefined };

                  return (
                    <div key={portal.key}>
                      <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                        {portal.label}
                      </Label>
                      <div className="flex flex-col gap-2">
                        <div>
                          <Label className="text-xs text-gray-600">
                            {portal.label} IN
                          </Label>
                          <select
                            value={sourceValue.in || ""}
                            onChange={(e) => handleSourceChange(portal.key, e.target.value, "in")}
                            className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                          >
                            <option value="">{`Selecciona una fuente para ${portal.label} IN`}</option>
                            {availableLocationSources.map((source) => (
                              <option key={source.locationSourceId} value={source.locationSourceId}>
                                {source.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600">
                            {portal.label} OUT
                          </Label>
                          <select
                            value={sourceValue.out || ""}
                            onChange={(e) => handleSourceChange(portal.key, e.target.value, "out")}
                            className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                          >
                            <option value="">{`Selecciona una fuente para ${portal.label} OUT`}</option>
                            {availableLocationSources.map((source) => (
                              <option key={source.locationSourceId} value={source.locationSourceId}>
                                {source.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="mt-4 flex flex-row gap-2">
                  <Button
                    variant="main"
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving || !isConfigurationComplete()}
                  >
                    {isSaving && <Loader2 className="size-4 animate-spin" />}
                    Guardar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsEditing(false);
                      // Reset to saved values
                      if (crmConnection?.smarthomeRentals?.locationSources) {
                        const existingSources: Record<
                          string,
                          string | { in?: string; out?: string }
                        > = {};
                        PORTAL_SOURCES.forEach((portal) => {
                          const value = crmConnection.smarthomeRentals.locationSources[portal.key];
                          if (value) {
                            existingSources[portal.key] = value;
                          }
                        });
                        setLocationSources(existingSources);
                      }
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col gap-2">
            {hasConfiguration() ? (
              PORTAL_SOURCES.map((portal) => {
                const locationSourceId =
                  crmConnection?.smarthomeRentals?.locationSources?.[portal.key];
                const locationSourceName = getLocationSourceName(locationSourceId);
                if (!locationSourceName) return null;
                return (
                  <div key={portal.key} className="flex items-center justify-between text-xs">
                    <p className="font-medium text-gray-600">
                      {portal.label}:
                    </p>
                    <p className="text-gray-700">{locationSourceName}</p>
                  </div>
                );
              })
            ) : (
              <p className="text-xs text-gray-500">
                Haz clic en Editar para configurar las variables globales.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalVariablesConfiguration;
